import { createFileRoute, Navigate, Link } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { queryDocs, removeDoc } from "@/integrations/firebase/data";
import { useAuth } from "@/hooks/use-auth";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Copy, Plus, Trash2, CheckCircle2, ArrowRight, Building2, Sparkles } from "lucide-react";
import { saveDealership, addDid, discoverIvrFromCdrs, applyDiscoveredPaths, type DiscoveredPath } from "@/integrations/firebase/admin-ops";

export const Route = createFileRoute("/_app/onboarding")({
  head: () => ({ meta: [{ title: "Onboarding — EESWEB" }] }),
  component: OnboardingPage,
});

// Steps that make up onboarding. Branches / departments / employees and the
// IVR key-press map are managed on their own dedicated screens (linked from
// here) so the flow stays a checklist rather than one giant form.
const STEPS = [
  "Dealership",
  "Phone numbers (DIDs)",
  "Branches & teams",
  "IVR options & routing",
  "Review & activate",
] as const;

// created_at is a Firestore Timestamp when written by the app, an ISO string
// when written by scripts — order by instant either way.
function millis(v: unknown): number {
  if (v && typeof (v as { toMillis?: unknown }).toMillis === "function") return (v as { toMillis: () => number }).toMillis();
  const t = v ? new Date(v as string).getTime() : 0;
  return Number.isFinite(t) ? t : 0;
}

function OnboardingPage() {
  const { role, loading } = useAuth();
  const qc = useQueryClient();
  const [activeClientId, setActiveClientId] = useState<string | null>(null);
  const [step, setStep] = useState(0);

  const { data: clients } = useQuery({
    queryKey: ["onboarding-clients"],
    queryFn: async () =>
      (await queryDocs<any>("clients", {})).sort((a, b) => millis(b.created_at) - millis(a.created_at)),
  });

  if (loading) return null;
  if (role !== "admin") return <Navigate to="/dashboard" replace />;

  const activeClient = clients?.find((c) => c.id === activeClientId) ?? null;

  return (
    <div className="mx-auto max-w-4xl space-y-5">
      <div>
        <h1 className="font-display text-2xl md:text-3xl font-semibold tracking-tight">Dealership onboarding</h1>
        <p className="text-sm text-muted-foreground">
          Set up a new dealership panel end to end — no code required. Each dealership's data,
          users and configuration stay separate.
        </p>
      </div>

      {!activeClient ? (
        <ClientChooser
          clients={clients ?? []}
          onPick={(id) => { setActiveClientId(id); setStep(0); }}
          onCreated={(id) => { qc.invalidateQueries({ queryKey: ["onboarding-clients"] }); setActiveClientId(id); setStep(1); }}
        />
      ) : (
        <>
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-2">
              <Building2 className="size-4 text-primary" />
              <span className="font-semibold">{activeClient.name}</span>
              <Badge variant={activeClient.onboarding_status === "active" ? "secondary" : "outline"}>
                {activeClient.onboarding_status}
              </Badge>
            </div>
            <Button variant="ghost" size="sm" onClick={() => setActiveClientId(null)}>← Back to list</Button>
          </div>

          <Stepper step={step} setStep={setStep} />

          {step === 0 && <StepDealership client={activeClient} onSaved={() => { qc.invalidateQueries({ queryKey: ["onboarding-clients"] }); setStep(1); }} />}
          {step === 1 && <StepDids clientId={activeClient.id} onNext={() => setStep(2)} />}
          {step === 2 && <StepLink title="Branches, departments & employees" desc="Add the dealership's locations, teams and staff." to="/branches" onNext={() => setStep(3)} />}
          {step === 3 && <StepIvr clientId={activeClient.id} onNext={() => setStep(4)} />}
          {step === 4 && <StepReview client={activeClient} onActivated={() => qc.invalidateQueries({ queryKey: ["onboarding-clients"] })} />}
        </>
      )}
    </div>
  );
}

function Stepper({ step, setStep }: { step: number; setStep: (n: number) => void }) {
  return (
    <div className="flex items-center gap-1 flex-wrap">
      {STEPS.map((label, i) => (
        <button
          key={label}
          onClick={() => setStep(i)}
          className={`flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${
            i === step ? "bg-primary text-primary-foreground" : i < step ? "bg-primary/10 text-primary" : "bg-muted text-muted-foreground"
          }`}
        >
          <span className="grid size-4 place-items-center rounded-full bg-background/30 text-xs">{i + 1}</span>
          {label}
        </button>
      ))}
    </div>
  );
}

function ClientChooser({
  clients, onPick, onCreated,
}: { clients: any[]; onPick: (id: string) => void; onCreated: (id: string) => void }) {
  const [name, setName] = useState("");
  const [creating, setCreating] = useState(false);
  const create = async () => {
    if (!name.trim()) return toast.error("Enter a dealership name");
    setCreating(true);
    try {
      const { id } = await saveDealership({ name: name.trim(), onboardingStatus: "draft", isActive: true });
      toast.success("Dealership created — continue setup");
      setName("");
      onCreated(id);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed");
    } finally {
      setCreating(false);
    }
  };
  return (
    <div className="space-y-4">
      <Card className="border-primary/30">
        <CardHeader className="pb-2"><CardTitle className="text-base">Start a new dealership</CardTitle></CardHeader>
        <CardContent className="flex gap-2 items-end">
          <div className="flex-1"><Label>Dealership name</Label><Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Viraj TVS" /></div>
          <Button onClick={create} disabled={creating}><Plus className="size-4 mr-1" /> {creating ? "Creating…" : "Create"}</Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Continue an existing dealership</CardTitle></CardHeader>
        <CardContent className="p-0">
          <div className="divide-y divide-border">
            {clients.map((c) => (
              <button key={c.id} onClick={() => onPick(c.id)} className="w-full flex items-center gap-3 p-3 text-left hover:bg-accent/40 text-sm">
                <Building2 className="size-4 text-muted-foreground" />
                <span className="font-medium">{c.name}</span>
                <Badge variant={c.onboarding_status === "active" ? "secondary" : "outline"} className="text-xs">{c.onboarding_status}</Badge>
                {!c.is_active && <Badge variant="outline" className="text-xs text-muted-foreground">inactive</Badge>}
                <ArrowRight className="size-3.5 ml-auto text-muted-foreground" />
              </button>
            ))}
            {clients.length === 0 && <div className="p-3 text-sm text-muted-foreground">No dealerships yet.</div>}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function StepDealership({ client, onSaved }: { client: any; onSaved: () => void }) {
  const [name, setName] = useState(client.name ?? "");
  const [fallback, setFallback] = useState(client.fallback_number ?? "");
  const [active, setActive] = useState(client.is_active ?? true);
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      await saveDealership({ id: client.id, name: name.trim(), fallbackNumber: fallback.trim() || null, isActive: active });
      toast.success("Saved");
      onSaved();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed");
    } finally {
      setSaving(false);
    }
  };
  return (
    <Card>
      <CardHeader className="pb-2"><CardTitle className="text-base">Dealership details</CardTitle></CardHeader>
      <CardContent className="space-y-3">
        <div><Label>Name</Label><Input value={name} onChange={(e) => setName(e.target.value)} /></div>
        <div>
          <Label>Default fallback number</Label>
          <Input value={fallback} onChange={(e) => setFallback(e.target.value)} placeholder="Rung when a specific option has no number / is unavailable" />
        </div>
        <div className="flex items-center gap-2">
          <Switch checked={active} onCheckedChange={setActive} id="active" />
          <Label htmlFor="active" className="cursor-pointer">Dealership active</Label>
        </div>
        <Button onClick={save} disabled={saving}>{saving ? "Saving…" : "Save & continue"}</Button>
      </CardContent>
    </Card>
  );
}

function StepDids({ clientId, onNext }: { clientId: string; onNext: () => void }) {
  const qc = useQueryClient();
  const [did, setDid] = useState("");
  const [label, setLabel] = useState("");
  const { data: dids } = useQuery({
    queryKey: ["onboarding-dids", clientId],
    queryFn: async () => (await queryDocs<any>("client_dids", { clientId })).sort((a, b) => String(a.did_number).localeCompare(String(b.did_number))),
  });
  const add = async () => {
    if (!did.trim()) return toast.error("Enter a DID number");
    try {
      // addDid refuses a number already assigned to any dealership.
      await addDid({ clientId, didNumber: did, label });
    } catch (e) { return toast.error(e instanceof Error ? e.message : "Failed"); }
    setDid(""); setLabel("");
    qc.invalidateQueries({ queryKey: ["onboarding-dids", clientId] });
    toast.success("DID added");
  };
  const remove = async (id: string) => {
    try { await removeDoc("client_dids", id); } catch (e) { return toast.error(e instanceof Error ? e.message : "Failed"); }
    qc.invalidateQueries({ queryKey: ["onboarding-dids", clientId] });
  };
  return (
    <Card>
      <CardHeader className="pb-2"><CardTitle className="text-base">Phone numbers (DIDs)</CardTitle></CardHeader>
      <CardContent className="space-y-3">
        <p className="text-sm text-muted-foreground">
          The dealership's virtual numbers. Incoming calls are matched to this dealership by their
          DID, so at least one is required for calls to appear.
        </p>
        <div className="flex gap-2 items-end flex-wrap">
          <div className="flex-1 min-w-[180px]"><Label>DID number</Label><Input value={did} onChange={(e) => setDid(e.target.value)} placeholder="e.g. 918045678900" /></div>
          <div className="flex-1 min-w-[140px]"><Label>Label (optional)</Label><Input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="e.g. Main line" /></div>
          <Button onClick={add}><Plus className="size-4 mr-1" /> Add</Button>
        </div>
        <div className="divide-y divide-border border border-border rounded-md">
          {dids?.map((d) => (
            <div key={d.id} className="flex items-center gap-2 p-2.5 text-sm">
              <code className="mono bg-muted/40 px-2 py-1 rounded">{d.did_number}</code>
              {d.label && <span className="text-muted-foreground">{d.label}</span>}
              <Button size="icon" variant="ghost" className="ml-auto h-7 w-7" onClick={() => remove(d.id)}><Trash2 className="size-3.5" /></Button>
            </div>
          ))}
          {(!dids || dids.length === 0) && <div className="p-3 text-sm text-muted-foreground">No DIDs yet.</div>}
        </div>
        <Button variant="outline" onClick={onNext}>Continue <ArrowRight className="size-4 ml-1" /></Button>
      </CardContent>
    </Card>
  );
}

function StepLink({ title, desc, to, onNext }: { title: string; desc: string; to: string; onNext: () => void }) {
  return (
    <Card>
      <CardHeader className="pb-2"><CardTitle className="text-base">{title}</CardTitle></CardHeader>
      <CardContent className="space-y-3">
        <p className="text-sm text-muted-foreground">{desc}</p>
        <p className="text-xs text-muted-foreground">
          Make sure the correct dealership is selected in the top-bar client selector, then use the
          dedicated screen:
        </p>
        <div className="flex gap-2">
          <Button asChild variant="default"><Link to={to}>Open {title.split(" ")[0]} screen</Link></Button>
          <Button variant="outline" onClick={onNext}>Done — continue <ArrowRight className="size-4 ml-1" /></Button>
        </div>
      </CardContent>
    </Card>
  );
}

function StepIvr({ clientId, onNext }: { clientId: string; onNext: () => void }) {
  const qc = useQueryClient();
  const [result, setResult] = useState<{ paths: DiscoveredPath[]; scanned: number; totalCalls: number; rootMenus: string[] } | null>(null);
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);

  const discover = async () => {
    setLoading(true);
    try {
      const res = await discoverIvrFromCdrs({ clientId, days: 30 });
      setResult(res);
      if (res.paths.length === 0) toast.info(`No key-press sequences found in ${res.totalCalls} call(s) from the last 30 days.`);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Discovery failed");
    } finally {
      setLoading(false);
    }
  };

  const apply = async () => {
    if (!result) return;
    setApplying(true);
    try {
      const r = await applyDiscoveredPaths({ clientId, paths: result.paths });
      toast.success(`Created ${r.created} menu option(s)${r.skipped ? `, ${r.skipped} already existed` : ""}`);
      qc.invalidateQueries();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to create menu");
    } finally {
      setApplying(false);
    }
  };

  const depth = (k: string) => k.split(">").length - 1;

  return (
    <Card>
      <CardHeader className="pb-2"><CardTitle className="text-base">IVR options & routing numbers</CardTitle></CardHeader>
      <CardContent className="space-y-3">
        <p className="text-sm text-muted-foreground">
          Define the key-press menu and the number each option rings. Make sure the correct
          dealership is selected in the top-bar client selector, then use the dedicated screen.
        </p>
        <div className="flex gap-2 flex-wrap">
          <Button asChild variant="default"><Link to="/routing">Open Routing screen</Link></Button>
          <Button variant="outline" onClick={onNext}>Done — continue <ArrowRight className="size-4 ml-1" /></Button>
        </div>

        <div className="rounded-md border border-border p-3 space-y-2" data-testid="ivr-discovery">
          <div className="flex items-center gap-2 text-sm font-medium">
            <Sparkles className="size-4 text-primary" /> Build the menu from call history
          </div>
          <p className="text-xs text-muted-foreground">
            Reads the last 30 days of calls to this dealership's numbers from SmartFlo and rebuilds the
            menu callers actually used — each key, where it led and who it rang. Review it, then create
            the options in one click. Existing options are never overwritten.
          </p>
          <div className="flex gap-2 flex-wrap">
            <Button size="sm" variant="secondary" onClick={discover} disabled={loading} data-testid="discover-btn">
              {loading ? "Scanning SmartFlo…" : "Scan last 30 days"}
            </Button>
            {result && result.paths.length > 0 && (
              <Button size="sm" onClick={apply} disabled={applying} data-testid="apply-discovery-btn">
                {applying ? "Creating…" : `Create ${result.paths.length} menu option(s)`}
              </Button>
            )}
          </div>
          {result && result.paths.length > 0 && (
            <div className="overflow-x-auto border border-border rounded-md mt-2">
              <table className="w-full text-sm" data-testid="discovery-table">
                <thead className="text-left text-xs uppercase tracking-wider text-muted-foreground border-b border-border">
                  <tr><th className="p-2">Keys</th><th className="p-2">Leads to</th><th className="p-2">Type</th><th className="p-2">Rings</th><th className="p-2">Calls</th></tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {result.paths.map((p) => (
                    <tr key={p.keyPath}>
                      <td className="p-2 whitespace-nowrap" style={{ paddingLeft: `${0.5 + depth(p.keyPath) * 1}rem` }}>
                        <Badge variant="outline" className="mono text-xs">{p.keyPath}</Badge>
                      </td>
                      <td className="p-2">
                        {p.label ?? <span className="text-muted-foreground">—</span>}
                        {p.destinations.length > 1 && (
                          <div className="text-xs text-muted-foreground">also: {p.destinations.slice(1).join(", ")}</div>
                        )}
                      </td>
                      <td className="p-2 text-xs">{p.nodeType === "menu" ? "Sub-menu" : "Rings"}</td>
                      <td className="p-2 mono text-xs">{p.phoneNumber ?? "—"}</td>
                      <td className="p-2 mono">{p.count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {result && (
            <p className="text-xs text-muted-foreground" data-testid="discovery-summary">
              {result.scanned} of {result.totalCalls} call(s) had key-presses
              {result.rootMenus.length > 0 && <> · main menu: {result.rootMenus.join(", ")}</>}
            </p>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function StepReview({ client, onActivated }: { client: any; onActivated: () => void }) {
  const webhookUrl = `${origin}/api/public/smartflo`;
  const [activating, setActivating] = useState(false);
  const copy = (t: string) => { navigator.clipboard.writeText(t); toast.success("Copied"); };
  const activate = async () => {
    setActivating(true);
    try {
      await saveDealership({ id: client.id, name: client.name, onboardingStatus: "active", isActive: true });
      toast.success("Dealership activated");
      onActivated();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed");
    } finally {
      setActivating(false);
    }
  };
  return (
    <Card>
      <CardHeader className="pb-2"><CardTitle className="text-base">Review & activate</CardTitle></CardHeader>
      <CardContent className="space-y-4">
        <div className="rounded-md border border-primary/30 bg-primary/5 p-3 space-y-2">
          <div className="text-sm font-medium">Hand this webhook URL to your telephony provider (Tata SmartFlo)</div>
          <div className="flex gap-2 items-center">
            <code className="flex-1 text-xs mono bg-muted/40 p-2 rounded truncate">{webhookUrl}</code>
            <Button size="icon" variant="ghost" onClick={() => copy(webhookUrl)}><Copy className="size-3.5" /></Button>
          </div>
          <p className="text-xs text-muted-foreground">
            Calls are matched to this dealership by their DID. This panel monitors and reports on
            calls; it does not reprogram the live IVR in SmartFlo.
          </p>
        </div>
        <Button onClick={activate} disabled={activating}>
          <CheckCircle2 className="size-4 mr-1" /> {activating ? "Activating…" : "Activate dealership"}
        </Button>
      </CardContent>
    </Card>
  );
}
